  /**
   * Lightweight sanitization for individual AI note field values.
   *
   * Only converts literal "\n" escape sequences to real newline characters
   * and strips stray Markdown bold markers (**). Does NOT trim, strip quotes,
   * or normalize bullets — those transforms are too aggressive for individual
   * field values and can cause content loss or layout issues.
   *
   * Used by:
   *   - AINotesView.js  → ControlRenderer (field value display)
   *   - AINotesView.js  → buildCopyModelForSelected (copy-to-clipboard)
   */
  export function sanitizeNoteFieldValue(val) {
    if (val == null) return "";
    let s = String(val);
    // Convert literal \n escape sequences to real newlines
    s = s.replace(/\\n/g, "\n");
    // Remove Markdown bold markers
    s = s.replace(/\*\*/g, "");
    return s;
  }

  /**
   * Normalize raw clinical content from the backend AI note API.
   *
   * Accepts a plain string, or an object with `.data.content` / `.content`.
   * Performs the following sanitizations:
   *   1. Strips surrounding double-quotes (JSON artefact)
   *   2. Converts literal "\n" escape sequences to real newline characters
   *   3. Removes stray Markdown bold markers (**) left by the LLM
   *   4. Normalizes bullet characters (• → -)
   *
   * Used by:
   *   - parseClinical()  → full clinical outline parsing
   */
  export function normalizeClinicalContent(input) {
    let text = input?.data?.content ?? input?.content ?? input ?? "";
    text = String(text).trim();
    text = text.replace(/^"|"$/g, "");   // surrounding quotes
    text = text.replace(/\\n/g, "\n");   // unescape newlines
    text = text.replace(/\*\*/g, "");    // remove bold markers
    // Optional: normalize bullets
    text = text.replace(/^\s*•\s+/gm, "- ");
    return text;
  }
  
  function tokenizeLines(text) {
    const lines = text.split(/\r?\n/);
  
    const tokens = [];
    let prevWasBlank = true;
  
    for (const raw of lines) {
      const line = raw.replace(/\s+$/g, ""); // rtrim
      const trimmed = line.trim();
  
      if (!trimmed) {
        tokens.push({ type: "blank", raw });
        prevWasBlank = true;
        continue;
      }
  
      // Markdown # headings
      const md = trimmed.match(/^(#{1,6})\s+(.*)$/);
      if (md) {
        tokens.push({ type: "heading", level: md[1].length, text: md[2], raw });
        prevWasBlank = false;
        continue;
      }
  
      // Numbered heading like "1. Something"
      const numH = trimmed.match(/^(\d+)\.\s+(.*)$/);
      if (numH && prevWasBlank) {
        tokens.push({ type: "heading", level: 2, text: numH[2], raw });
        prevWasBlank = false;
        continue;
      }
  
      // Colon-style heading: "Some Heading:" (not a bullet)
      if (/^[^\-\*\d][^:]{0,100}:\s*$/.test(trimmed) && prevWasBlank) {
        tokens.push({
          type: "heading",
          level: 1, // top-level heuristic; will nest later
          text: trimmed.replace(/:\s*$/, ""),
          raw,
        });
        prevWasBlank = false;
        continue;
      }
  
      // Bullet (-, *, •) with optional indent
      const bul = raw.match(/^(\s*)([-*])\s+(.*)$/);
      if (bul) {
        const indent = Math.floor((bul[1] || "").replace(/\t/g, "  ").length / 2); // 2 spaces = 1 level
        tokens.push({ type: "bullet", indent, text: bul[3].trim(), raw });
        prevWasBlank = false;
        continue;
      }
  
      // Paragraph / other line (dates, inline lists, etc.)
      tokens.push({ type: "paragraph", text: trimmed, raw });
      prevWasBlank = false;
    }
  
    return tokens;
  }
  
  /**
   * Build an outline/tree from tokens.
   * Sections are created from headings; bullets/paragraphs attach to nearest section.
   * Bullet items support nested subitems via indentation.
   */
  export function parseClinicalOutline(text) {
    const tokens = tokenizeLines(text);
  
    const root = { type: "section", title: "Root", level: 0, content: [], items: [], children: [] };
    const sectionStack = [root];
  
    // Helper to get current section
    const curSection = () => sectionStack[sectionStack.length - 1];
  
    // Manage an active bullet list stack per section
    let listStack = []; // [{indent, items:[]}]
    const ensureTopList = () => {
      if (!curSection().items) curSection().items = [];
      if (listStack.length === 0) {
        const lst = { indent: 0, items: [] };
        listStack.push(lst);
        curSection().items.push(lst);
      }
    };
    const closeListsTo = (indent) => {
      while (listStack.length && listStack[listStack.length - 1].indent > indent) {
        listStack.pop();
      }
    };
  
    for (const t of tokens) {
      if (t.type === "heading") {
        // Close any open lists when new section starts
        listStack = [];
  
        // Determine nesting: higher level → children; lower level → pop
        let level = t.level;
        // Heuristic: if we're already inside a top-level, treat new headings w/o # as level 2
        if (t.level === 1 && sectionStack.length > 1) level = 2;
  
        while (sectionStack.length && sectionStack[sectionStack.length - 1].level >= level) {
          sectionStack.pop();
        }
  
        const node = {
          type: "section",
          title: t.text,
          level,
          content: [],
          items: [],
          children: [],
        };
        curSection().children.push(node);
        sectionStack.push(node);
        continue;
      }
  
      if (t.type === "bullet") {
        ensureTopList();
  
        // Adjust list nesting by indent
        closeListsTo(t.indent);
        if (listStack.length === 0 || listStack[listStack.length - 1].indent < t.indent) {
          const newList = { indent: t.indent, items: [] };
          // Attach to previous item of the parent list
          const parentList = listStack[listStack.length - 1];
          const lastItem = parentList?.items[parentList.items.length - 1];
          if (lastItem) {
            lastItem.sublist = lastItem.sublist || [];
            lastItem.sublist.push(newList);
          } else {
            curSection().items.push(newList);
          }
          listStack.push(newList);
        }
  
        // Add item to current top list
        listStack[listStack.length - 1].items.push({ text: t.text });
        continue;
      }
  
      if (t.type === "paragraph") {
        listStack = []; // paragraph breaks lists in this simple model
        curSection().content.push(t.text);
        continue;
      }
    }
  
    return root.children.length ? root.children : [root];
  }
  
  /**
   * Convenience: fully parse from raw input (string or {content})
   */
  export function parseClinical(input) {
    const text = normalizeClinicalContent(input);
    return parseClinicalOutline(text);
  }
  